import { useQueryClient } from 'react-query';
import { useCollections } from 'hooks/useCollections';
import { useDefaultCollections } from 'hooks/useDefaultCollections';
import { useProjectData } from 'hooks/useProjectsByCategory';
import { Project } from 'types';
import type { SavedProject } from 'types/SavedProject';

type TProjectSlug = Project['slug'];

type TAllHiddenSet = Set<string>;

type TSavedDictionary = Record<string, SavedProject>;

/* This hook handles optmistic updates for hidden projects and saved projects.
 * These functions are in a centralize hook because they are shared between the
 * hooks that handle most of hidden and saved interactions; we want to avoid
 * circular reference.
 */
export const useHideAndSaveProjectInteraction = () => {
  const { invalidateGetNumSavedProjectsByCategory } = useDefaultCollections();
  const { invalidateGetGroupConfigLeadSummary, invalidateGetCollectionSummary } =
    useProjectData();
  const { invalidateGetCollectionsList } = useCollections();
  const queryClient = useQueryClient();

  const snapshotHiddenProjectIds = async () => {
    await queryClient.cancelQueries({ queryKey: ['hidden-projects'] });

    return queryClient.getQueryData<TAllHiddenSet>(['hidden-projects']);
  };

  const optimisticallyAddHiddenProject = async (projectSlug: TProjectSlug) => {
    const previousHiddenProjects = await snapshotHiddenProjectIds();
    if (previousHiddenProjects) {
      previousHiddenProjects.add(projectSlug);
      queryClient.setQueryData<TAllHiddenSet>(
        ['hidden-projects'],
        new Set(previousHiddenProjects),
      );
    }
  };

  const optimisticallyRemoveHiddenProject = async (projectSlug: TProjectSlug) => {
    const previousHiddenProjects = await snapshotHiddenProjectIds();
    if (previousHiddenProjects) {
      previousHiddenProjects.delete(projectSlug);
      queryClient.setQueryData<TAllHiddenSet>(
        ['hidden-projects'],
        new Set(previousHiddenProjects),
      );
    }
  };

  const snapshotSavedProjects = async () => {
    return queryClient.getQueryData<TSavedDictionary>(['all-saved-projects']);
  };

  const optimisticallyRemoveSavedProject = async (projectSlug: TProjectSlug) => {
    const previousSavedProjects = await snapshotSavedProjects();
    if (previousSavedProjects && previousSavedProjects[projectSlug]) {
      delete previousSavedProjects[projectSlug];
      queryClient.setQueryData<TSavedDictionary>(
        ['all-saved-projects'],
        previousSavedProjects,
      );
    }
  };

  /* Trigger all refetches related to actioning a saved project. */
  const actionSavedProjectTriggerRefetch = async () => {
    invalidateGetNumSavedProjectsByCategory();
    invalidateGetGroupConfigLeadSummary();
    invalidateGetCollectionSummary();
    invalidateGetCollectionsList();
  };

  /* Trigger all refetches related to removing a saved project. */
  const removeHiddenProjectTriggerRefetch = async () => {
    invalidateGetGroupConfigLeadSummary();
    invalidateGetCollectionSummary();
  };

  return {
    optimisticallyAddHiddenProject,
    optimisticallyRemoveHiddenProject,
    optimisticallyRemoveSavedProject,
    actionSavedProjectTriggerRefetch,
    removeHiddenProjectTriggerRefetch,
  };
};
